// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TippyPopupStyle } from "@electrowire/App/Components/Tippy/Style";
import TippyPopup from "@electrowire/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import { headerLinkListState } from "@electrowire/Overrides/Widgets/Header/HeaderLinkList.helper";
import { color, xClassNames } from "@gcg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface MappedLink {
    title: string;
    url: string;
    type?: string;
    openInNewWindow?: boolean;
    maxDepth?: number;
    childrenType?: "CustomLinksList" | "CascadingMenu" | "Link";
    children?: MappedLink[];
    overrideDestinationUrl?: string;
    trigger?: string;
    onClick?: () => void;
}

const enum fields {
    links = "links",
    customLinks = "customLinks",
    customCSS = "customCSS",
}

export interface HeaderLinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        title: string;
        linkType: "Link" | "CascadingMenu" | "CustomLinksList";
        overrideTitle: string;
        customLinks?: any;
    };
}

interface OwnProps extends WidgetProps {
    fields: { 
        [fields.links]: HeaderLinkModel[]; 
        [fields.customCSS]: string 
    };
    extendedStyles?: HeaderLinkListStyles;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const brandsDataView = getAllBrandsDataView(state);
    return {
        shouldLoadBrands: !brandsDataView.value && !brandsDataView.isLoading,
        links: headerLinkListState(state, ownProps.fields.links),
    };
};

const mapDispatchToProps = {
    loadAllBrands,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // XNG-Changes
    tippyStyles?: TippyPopupStyle;
    menuWrapper?: InjectableCss;
    childLink?: LinkPresentationProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            margin-right: 14px;
            display: flex;
            flex-direction: row-reverse;
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
            size: 14,
            lineHeight: "16px",
            weight: 600,
            transform: "capitalize",
            className: "websitenavbar",
            css: css`
                display: inline-block;
                &&& {
                    color: ${getColor("text.main")};
                }
            `,
        } as any,
        css: css`
            margin: 0 14px 0 14px;
            &.cascadingMenu-link {
                margin: 0 10px 0 14px;
            }
            &:hover > ${TypographyStyle} {
                text-decoration: none;
            }
        `,
    },
    childLink: {
        color: "text.main",
        typographyProps: {
            size: 14,
            weight: 600,
            transform: "capitalize",
        },
        className: "websitenavbar",
        css: css`
            &:hover > ${TypographyStyle} {
                text-decoration: none;
                color: ${color("common.ui8")};
            }
        `,
    } as any,
    tippyStyles: {
        main: {
            css: css`
                &.tippy-box {
                    border: 0;
                    margin-top: -3px;
                    & > .tippy-content {
                        background-color: ${color("common.ui2")};
                        max-height: 300px;
                    }
                }
            `,
        },
        trigger: {
            css: css`
                & > ${StyledButton} > ${TypographyStyle}, & > ${StyledButton} > ${IconWrapper} {
                    transition: transform ease-in-out 250ms;
                }

                &[aria-expanded="true"] {
                    & > ${StyledButton} > ${IconWrapper} {
                        transform: rotate(180deg);
                        color: ${color("common.ui8")};
                    }
                    & > ${StyledButton} > ${TypographyStyle} {
                        color: ${color("common.ui8")};
                    }
                }
            `,
        },
    },
    menuWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 15px;
            width: 100%;
        `,
    },
};

const styles = headerLinkListStyles;

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

export const HeaderLinkList: React.FC<Props> = ({ fields, loadAllBrands, links, shouldLoadBrands, extendedStyles }) => {
    // XNG-Changes
    const loadBrandsIfNeeded = () => {
        if (shouldLoadBrands) {
            loadAllBrands({
                sort: "name asc",
                select: "htmlContent,topSellerProducts,id,detailPagePath,name,properties",
                expand: "properties",
            });
        }
    };

    // XNG-Changes
    useEffect(() => {
        if (shouldLoadBrands) {
            loadBrandsIfNeeded();
        }
    }, [shouldLoadBrands]);

    if (links.length < 1) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper
                className="list-wrapper"
                {...styles.headerLinkListWrapper}
                data-test-selector="HeaderLinkList"
            >
                {links.map((link, index) => {
                    if (!link.url) {
                        return null;
                    }

                    const isCascadingMenu =
                        link.childrenType === "CascadingMenu" && link.children && link.children.length > 0;

                    const isCustomLinks = link.childrenType === "CustomLinksList";

                    const linkElement = (
                        <Link
                            key={`${link.url}.${link.title}`}
                            href={link.url}
                            target={link.openInNewWindow ? "_blank" : ""}
                            icon={{
                                iconProps: {
                                    src: isCascadingMenu || isCustomLinks ? ChevronDown : undefined,
                                    size: 16,
                                    color: "common.ui4",
                                },
                                position: "right",
                            }}
                            className={xClassNames.append(isCascadingMenu || isCustomLinks ? "cascadingMenu-link" : "")}
                            {...styles.link}
                        >
                            {link.title}
                        </Link>
                    );

                    if (isCascadingMenu || isCustomLinks) {
                        const menu = (
                            <StyledWrapper {...styles.menuWrapper}>
                                {link.children?.map(childLink => (
                                    <Link
                                        key={`${childLink.url}.${childLink.title}`}
                                        href={childLink.url}
                                        target={childLink.openInNewWindow ? "_blank" : ""}
                                        {...styles.childLink}
                                    >
                                        {childLink.title}
                                    </Link>
                                ))}
                            </StyledWrapper>
                        );

                        return (
                            <TippyPopup
                                key={`${link.url}.${link.title}`}
                                content={menu}
                                interactive={true}
                                appendTo="parent"
                                arrow={false}
                                offset={[0, 13]}
                                maxWidth={340}
                                placement="bottom-start"
                                showCloseButton={false}
                                extendedStyles={styles.tippyStyles}
                            >
                                {linkElement}
                            </TippyPopup>
                        );
                    }
                    return linkElement;
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Cascading Menu",
                                value: "CascadingMenu",
                            },
                            {
                                displayName: "Custom Links List",
                                value: "CustomLinksList",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: item => item.fields.destination.type === "Url",
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.destination.type !== "Url",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: fields.customLinks,
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields, state) => {
                            const {
                                destination: { type, value },
                                overriddenTitle,
                            } = item.fields;
                            if (overriddenTitle) {
                                return overriddenTitle;
                            }
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return link?.title;
                            }
                            if (type === "Category") {
                                let displayValue = "";
                                const categories = (state as any).pageEditor.categories;
                                if (!categories) {
                                    displayValue = "Loading...";
                                } else {
                                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                                    if (matchingCategories.length === 0) {
                                        displayValue = "Unknown";
                                    } else {
                                        displayValue = matchingCategories[0].shortDescription;
                                    }
                                }

                                return displayValue;
                            }
                            if (type === "Url") {
                                return value;
                            }
                            return value;
                        },
                        defaultValue: [],
                        isVisible: item => item.fields.linkType === "CustomLinksList",
                        fieldDefinitions: [
                            {
                                name: "destination",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Page",
                                },
                                isRequired: true,
                            },
                            {
                                name: "title",
                                editorTemplate: "TextField",
                                defaultValue: "",
                                isRequired: true,
                                isVisible: item => item.fields.destination.type === "Url",
                            },
                            {
                                name: "overrideTitle",
                                editorTemplate: "TextField",
                                defaultValue: "",
                                isVisible: item => item.fields.destination.type !== "Url",
                            },
                            {
                                name: "openInNewWindow",
                                editorTemplate: "CheckboxField",
                                defaultValue: true,
                            },
                        ],
                        
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
